import { ContractorProfile, ContractorProfileUpdate, ContractorProfileCreate, Category } from '../types/contractor';
import { API_BASE } from './config';
import apiClient from './client';
import { apiCache, TTL } from './cache';

const API_BASE_URL = `${API_BASE}/api/ContractorProfile`;
const CATEGORIES_URL = `${API_BASE}/api/Categories`;

export interface ContractorFacets {
  provinceIds: number[];
  townIds: number[];
  categoryIds: number[];
}

function searchCacheKey(prefix: string, filters?: { categoryIds?: number[]; provinceId?: number; townId?: number }): string {
  const ids = [...(filters?.categoryIds ?? [])].sort().join(',');
  return `${prefix}:${ids}:${filters?.provinceId ?? ''}:${filters?.townId ?? ''}`;
}

export async function fetchContractorFacets(filters?: {
  categoryIds?: number[];
  provinceId?: number;
  townId?: number;
}): Promise<ContractorFacets> {
  const key = searchCacheKey('facets', filters);
  const cached = apiCache.get<ContractorFacets>(key);
  if (cached) return cached;

  const params = new URLSearchParams();
  if (filters?.categoryIds?.length) {
    filters.categoryIds.forEach(id => params.append('categoryIds', id.toString()));
  }
  if (filters?.provinceId) params.append('provinceId', filters.provinceId.toString());
  if (filters?.townId) params.append('townId', filters.townId.toString());
  const response = await fetch(`${API_BASE_URL}/facets?${params.toString()}`);
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const data: ContractorFacets = await response.json();
  apiCache.set(key, data, TTL.TWO_MIN);
  return data;
}

export async function fetchCategories(): Promise<Category[]> {
  const cached = apiCache.get<Category[]>('categories');
  if (cached) return cached;
  const response = await fetch(CATEGORIES_URL);
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const data: Category[] = await response.json();
  apiCache.set('categories', data, TTL.FOREVER);
  return data;
}

export async function createCategory(name: string): Promise<Category> {
  const response = await apiClient.post<Category>('/Categories', { name });
  return response.data;
}

export async function deleteCategory(id: number): Promise<void> {
  await apiClient.delete(`/Categories/${id}`);
}

export async function fetchContractorProfile(): Promise<ContractorProfile | null> {
  try {
    const response = await apiClient.get<ContractorProfile>('/ContractorProfile/me');
    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
}

export async function createContractorProfile(profile: ContractorProfileCreate): Promise<ContractorProfile> {
  const response = await apiClient.post<ContractorProfile>('/ContractorProfile', profile);
  return response.data;
}

export async function updateContractorProfile(profile: ContractorProfileUpdate): Promise<ContractorProfile> {
  const response = await apiClient.put<ContractorProfile>('/ContractorProfile', profile);
  return response.data;
}

export async function getContractors(filters?: {
  categoryIds?: number[];
  provinceId?: number;
  townId?: number;
}): Promise<ContractorProfile[]> {
  const key = searchCacheKey('contractors', filters);
  const cached = apiCache.get<ContractorProfile[]>(key);
  if (cached) return cached;

  const queryParams = new URLSearchParams();
  if (filters) {
    if (filters.categoryIds?.length) {
      filters.categoryIds.forEach(id => queryParams.append('categoryIds', id.toString()));
    }
    if (filters.provinceId) queryParams.append('provinceId', filters.provinceId.toString());
    if (filters.townId) queryParams.append('townId', filters.townId.toString());
  }
  const url = `${API_BASE_URL}?${queryParams.toString()}`;
  const response = await fetch(url);
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const data: ContractorProfile[] = await response.json();
  apiCache.set(key, data, TTL.TWO_MIN);
  return data;
}

export async function getContractorById(id: string): Promise<ContractorProfile> {
  const response = await fetch(`${API_BASE_URL}/${id}`);
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
}

export async function getFeaturedContractors(): Promise<ContractorProfile[]> {
  const cached = apiCache.get<ContractorProfile[]>('featured');
  if (cached) return cached;
  const response = await fetch(`${API_BASE_URL}/featured`);
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const data: ContractorProfile[] = await response.json();
  apiCache.set('featured', data, TTL.FIVE_MIN);
  return data;
}
